'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { useSession } from 'next-auth/react';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import { ListFilter } from 'lucide-react';
import axios from 'axios';

import { FanficCard } from '@/features/fanfic-card/components/fanfic-card';
import { resolveFandomCardFields } from '@/features/fanfic-card/utils/resolveFandomCardFields.util';
import { mapCharactersToCardFields } from '@/features/fanfic-card/utils/resolveCharacterCardFields.util';
import { resolveFanficPublishedAt } from '@/features/fanfic-card/utils/resolve-fanfic-published-at.util';
import type { FanficCardCharacter } from '@/features/fanfic-card/types/fanfic-card-character.types';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { getRandomPlaceholder } from '@/lib/fanfic-placeholders';
import { compactStrings, sanitizeTitle } from '@/lib/utils';
import { getAuthorWorksReq } from '@/features/author/api/author-works.requests';
import { hasUsableAuthorWorksSsrData } from '@/features/author/utils/author-works-request.util';
import { AUTHOR_WORKS_PAGE_LIMIT } from '@/features/author/utils/build-author-works-query.util';
import type {
  AuthorWorkResponse,
  AuthorWorksSortBy,
} from '@/features/author/types/author-works.types';

interface Pairing {
  id: string;
  type: string;
  characters: Array<{
    id: number;
    name: string;
    aliases: string[];
    fandom?: {
      id: number;
      title: string;
      sec_title: string;
      group_id: string;
      slug: string;
      is_universe: boolean;
      characters: unknown[];
      updatedAt: string;
      createdAt: string;
    };
    updatedAt: string;
    createdAt: string;
  }>;
}

interface AuthorFanfic {
  id: number;
  title: string;
  description?: string;
  rating?: string;
  chaptersCount?: number;
  score?: number;
  direction?: string;
  likes?: number;
  views?: number;
  date?: string;
  status?: string;
  cover?: string;
  createdAt?: string;
  publishedAt?: string;
  updatedAt?: string;
  userLiked?: boolean;
  type?: 'fanfic' | 'original';
  tags?: { id: string; title: string }[];
  characters?: FanficCardCharacter[];
  pairings?: Pairing[];
  size?: string;
  totalWordCount?: number;
  focus: string;
  fandom?: string;
  fandomId?: string;
  fandomPermanentUrl?: string;
}

const formatDate = (dateString?: string) => {
  if (!dateString) return '--.--.----';
  const date = new Date(dateString);
  return format(date, 'dd.MM.yy', { locale: ru });
};

const transformFanficData = (fanfic: AuthorWorkResponse): AuthorFanfic => {
  const fandomFields = resolveFandomCardFields(fanfic.fandom, fanfic.universe);

  return {
    id: fanfic.id,
    title: sanitizeTitle(fanfic.title) || 'Без названия',
    description: fanfic.description || 'Без описания',
    rating: fanfic.rating || 'G',
    chaptersCount:
      typeof fanfic.chaptersCount === 'number'
        ? fanfic.chaptersCount
        : Array.isArray(fanfic.chapters)
          ? fanfic.chapters.length
          : 0,
    score: fanfic.score || 0,
    direction: fanfic.direction || '♂ Гет',
    likes: fanfic.likesCount || 0,
    views: fanfic.readersCount || 0,
    date: (() => {
      const publishedAt = resolveFanficPublishedAt(fanfic);
      return publishedAt ? formatDate(publishedAt) : undefined;
    })(),
    status: fanfic['post-status'] || fanfic.status || 'В процессе',
    cover: fanfic?.heroImage?.url ? fanfic?.heroImage?.url : getRandomPlaceholder(fanfic.id),
    createdAt: fanfic.createdAt,
    publishedAt: fanfic.publishedAt,
    updatedAt: fanfic.updatedAt,
    userLiked: fanfic.userLiked || false,
    type: fanfic.type || 'fanfic',
    tags: fanfic.tags || [],
    characters: mapCharactersToCardFields(fanfic.characters),
    pairings: (fanfic.pairings || []) as Pairing[],
    size: fanfic.size,
    totalWordCount: fanfic.totalWordCount,
    focus: fanfic.focus || '',
    fandom: fandomFields.title,
    fandomId: fandomFields.id,
    fandomPermanentUrl: fandomFields.permanentUrl,
  };
};

interface AuthorWorksProps {
  userId: string;
  authorName?: string;
  totalWorksCount?: number;
  onTotalWorksCountChange?: (count: number) => void;
  initialFanfics?: AuthorWorkResponse[];
  initialHasMore?: boolean;
  initialLoadFailed?: boolean;
}

export function AuthorWorks({
  userId,
  authorName,
  totalWorksCount = 0,
  onTotalWorksCountChange,
  initialFanfics = [],
  initialHasMore = false,
  initialLoadFailed = false,
}: AuthorWorksProps) {
  const { data: session, status: sessionStatus } = useSession();
  const accessToken = session?.accessToken;
  const hasUsableSsrData = hasUsableAuthorWorksSsrData(initialLoadFailed);
  const [fanfics, setFanfics] = useState<AuthorFanfic[]>(
    hasUsableSsrData ? initialFanfics.map(transformFanficData) : [],
  );
  const [loading, setLoading] = useState(!hasUsableSsrData);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(initialHasMore);
  const [sortBy, setSortBy] = useState<AuthorWorksSortBy>('newest');
  const abortRef = useRef<AbortController | null>(null);
  const skipInitialFetchRef = useRef(hasUsableSsrData);

  const loadAuthorFanfics = useCallback(
    async (page: number, append: boolean, nextSortBy: AuthorWorksSortBy) => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      try {
        const data = await getAuthorWorksReq({
          userId,
          page,
          limit: AUTHOR_WORKS_PAGE_LIMIT,
          sortBy: nextSortBy,
          accessToken,
          signal: controller.signal,
        });

        if (controller.signal.aborted) {
          return;
        }

        onTotalWorksCountChange?.(data.totalDocs);

        const transformedFanfics = data.fanfics.map(transformFanficData);

        if (append) {
          setFanfics((prev) => [...prev, ...transformedFanfics]);
        } else {
          setFanfics(transformedFanfics);
          setCurrentPage(page);
        }

        setHasMore(data.hasNextPage);
        setError(null);
      } catch (err) {
        if (axios.isCancel(err) || (axios.isAxiosError(err) && err.code === 'ERR_CANCELED')) {
          return;
        }
        console.error('Failed to load author fanfics:', err);
        setError('Не удалось загрузить работы автора');
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    },
    [accessToken, onTotalWorksCountChange, userId],
  );

  const isWaitingForSession =
    !hasUsableSsrData && sessionStatus === 'loading' && fanfics.length === 0;

  useEffect(() => {
    if (skipInitialFetchRef.current) {
      return;
    }

    if (sessionStatus === 'loading') {
      return;
    }

    skipInitialFetchRef.current = true;
    let cancelled = false;
    queueMicrotask(() => {
      if (!cancelled) {
        void loadAuthorFanfics(1, false, 'newest');
      }
    });
    return () => {
      cancelled = true;
    };
  }, [loadAuthorFanfics, sessionStatus]);

  const handleSortChange = (nextSortBy: AuthorWorksSortBy) => {
    if (nextSortBy === sortBy) {
      return;
    }

    skipInitialFetchRef.current = true;
    setSortBy(nextSortBy);
    setCurrentPage(1);
    setFanfics([]);
    setLoading(true);
    setError(null);
    void loadAuthorFanfics(1, false, nextSortBy);
  };

  const handleLoadMore = () => {
    if (hasMore && !loading) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      setLoading(true);
      void loadAuthorFanfics(nextPage, true, sortBy);
    }
  };

  return (
    <div className="container mx-auto px-0 py-8">
      <div className="flex justify-between items-center mb-6">
        <p className="uppercase text-2xl font-black">РАБОТЫ АВТОРА ({totalWorksCount})</p>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              className="whitespace-nowrap bg-white h-full py-3 border-none"
            >
              <ListFilter className="w-4 h-4" />
              <span className="hidden sm:block">
                {sortBy === 'newest'
                  ? 'По дате создания'
                  : sortBy === 'popular'
                    ? 'По популярности'
                    : sortBy === 'best'
                      ? 'По количеству лайков'
                      : 'По дате обновления'}
              </span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            <DropdownMenuItem onClick={() => handleSortChange('newest')}>
              По дате создания
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleSortChange('popular')}>
              По популярности
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleSortChange('best')}>
              По количеству лайков
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleSortChange('updated')}>
              По дате обновления
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {error && (
        <div className="flex justify-center items-center py-12">
          <div className="text-red-500">{error}</div>
        </div>
      )}

      {(loading || isWaitingForSession) && fanfics.length === 0 && (
        <div className="flex justify-center items-center py-12">
          <div className="text-gray-500">Загрузка работ автора...</div>
        </div>
      )}

      {!error && fanfics.length === 0 && !loading && !isWaitingForSession && (
        <div className="text-center py-12">
          <div className="text-gray-500">
            {authorName
              ? `${authorName} пока не опубликовал работ`
              : 'У автора пока нет опубликованных работ'}
          </div>
        </div>
      )}

      {fanfics.length > 0 && (
        <>
          <div className="grid grid-cols-2 gap-2 min-[880px]:grid-cols-3 min-[880px]:gap-5 lg:grid-cols-4 xl:grid-cols-5 items-stretch">
            {fanfics.map((fanfic) => (
              <FanficCard
                key={fanfic.id}
                {...fanfic}
                tags={compactStrings(fanfic.tags?.map((tag) => tag?.title) || [])}
                characters={fanfic.characters}
                pairings={fanfic.pairings}
                fandom={fanfic.fandom}
              />
            ))}
          </div>

          {hasMore && (
            <div className="flex justify-center mt-8">
              <button
                onClick={handleLoadMore}
                disabled={loading}
                type="button"
                className="px-6 py-3 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? 'Загрузка...' : 'Показать еще'}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
